/**
 * Справочник должностей (`/positions`, только админ — p2-position-dictionary, canon по образцу
 * каталога программ обучения `training-programs.tsx`). Список + создать/переименовать/удалить
 * должность. DELETE → 409, если должность используется (FK RESTRICT в `workplaces`/профрисках) —
 * показываем пояснение. Служебные сентинел-записи (имя начинается на «(», напр. «(Должность не
 * указана)») админ должен видеть (в отличие от `PositionSelect`, который их прячет) — помечены
 * бейджем «служебная». Правит ТОЛЬКО админ; после успешной мутации сбрасывается модульный кэш
 * `PositionSelect` (`invalidatePositionsCache`), чтобы открытые в сессии формы увидели изменения.
 * Удаление — только после подтверждения в модальном диалоге (docs/changes-3/Правки_фаза_3…, п.1.1).
 *
 * Проверка и объединение (positions-dictionary-review): должности, которые клиенты ввели сами
 * (или пришли из импорта структуры), — «не проверена»: в подсказках выбора их не видят, пока
 * администратор не отметит «Проверена», не исправит («Править» тоже ставит отметку) или не
 * объединит с правильной («Объединить…»: ссылки рабочих мест и профрисков переходят на выбранную
 * должность, исходная удаляется; перед подтверждением — сколько мест и клиентов изменится и
 * предупреждение о двух местах одной должности в подразделении). Фильтр «Не проверенные (N)».
 * Справочник большой (≈ 7 тыс. записей после загрузки архива СОУТ) — клиентский поиск по подстроке и
 * показ порциями по `PAGE_SIZE` строк («Показать ещё»), чтобы страница не рисовала тысячи строк.
 *
 * Скрытие (positions-hide): должность, которую нельзя удалить (она уже в заказах и структуре клиента)
 * и не нужно исправлять, скрывается кнопкой «Скрыть»: из подсказок выбора и из очереди «Не
 * проверенные» она исчезает, запись и ссылки остаются. Скрытые — на вкладке «Скрытые (N)», там же
 * «Показать» возвращает прежнее состояние. Скрыть можно любую обычную должность, в том числе проверенную.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Briefcase, Trash2, X } from 'lucide-react'
import {
  adminApi,
  positionsApi,
  ApiError,
  isHiddenPosition,
  isVerifiedPosition,
  type Position,
  type PositionMergePreview,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { pluralRu } from '@/lib/orders'
import { invalidatePositionsCache, PositionSelect } from '@/company/position-select'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Card } from '@/design-system/components/layout/card'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Badge } from '@/design-system/components/primitives/badge'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/design-system/components/primitives/tabs'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

/** Служебная запись-сентинел («(Должность не указана)» и пр.) — префикс «(» в имени. */
function isSentinel(name: string): boolean {
  return name.trimStart().startsWith('(')
}

/** Сколько строк справочника показывать за раз (кнопка «Показать ещё» — следующая порция). */
export const PAGE_SIZE = 100

type Filter = 'all' | 'unverified' | 'hidden'

/** Очередь проверки: не проверена и не скрыта (скрытую администратор уже разобрал). */
function isPendingReview(p: Position): boolean {
  return !isVerifiedPosition(p) && !isHiddenPosition(p)
}

/** Сводка предпросмотра объединения для окна подтверждения. */
function mergePreviewText(p: PositionMergePreview): string {
  const parts = [
    p.workplaces > 0
      ? `${p.workplaces} ${pluralRu(p.workplaces, 'рабочее место', 'рабочих места', 'рабочих мест')} ` +
        `у ${p.clients} ${pluralRu(p.clients, 'клиента', 'клиентов', 'клиентов')}`
      : 'рабочих мест с этой должностью нет',
  ]
  if (p.profriskPositions > 0)
    parts.push(
      `${p.profriskPositions} ${pluralRu(p.profriskPositions, 'должность', 'должности', 'должностей')} в анкетах профрисков`,
    )
  if (p.profriskResults > 0)
    parts.push(
      `${p.profriskResults} ${pluralRu(p.profriskResults, 'результат', 'результата', 'результатов')} профрисков`,
    )
  return `Изменится: ${parts.join(', ')}.`
}

/** Заголовок пустого списка: пустая вкладка без поиска — «всё разобрано/нет скрытых», иначе «ничего не найдено». */
function emptyTitle(filter: Filter, search: string): string {
  if (search.trim()) return 'Ничего не найдено'
  if (filter === 'unverified') return 'Все должности проверены'
  if (filter === 'hidden') return 'Скрытых должностей нет'
  return 'Ничего не найдено'
}

function emptyDescription(filter: Filter, search: string): string {
  if (search.trim()) return 'Измените запрос поиска'
  if (filter === 'unverified') return 'Новые должности, которые введут клиенты, появятся здесь'
  if (filter === 'hidden') return 'Скрытая должность не показывается в подсказках; здесь её можно вернуть'
  return 'Измените запрос поиска'
}

function PositionForm({
  name,
  setName,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  name: string
  setName: (v: string) => void
  busy: boolean
  submitLabel: string
  onSubmit: () => void
  onCancel?: () => void
}) {
  const canSubmit = name.trim().length > 0

  function submit(e: FormEvent) {
    e.preventDefault()
    if (canSubmit && !busy) onSubmit()
  }

  return (
    <form onSubmit={submit} noValidate>
      <Field label="Название" required className="my-0">
        {(props) => (
          <Input
            {...props}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Напр. «Слесарь-ремонтник»"
          />
        )}
      </Field>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={!canSubmit || busy}>
          {busy ? 'Сохранение…' : submitLabel}
        </Button>
        {onCancel ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={onCancel}
            leftIcon={<X className="size-4 stroke-current" aria-hidden />}
          >
            Отмена
          </Button>
        ) : null}
      </div>
    </form>
  )
}

export function PositionsPage() {
  const [positions, setPositions] = useState<Position[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [shown, setShown] = useState(PAGE_SIZE)
  const [notice, setNotice] = useState<string | null>(null)

  /** Объединение: исходная должность, выбранная целевая, предпросмотр и ход выполнения. */
  const [mergeSource, setMergeSource] = useState<Position | null>(null)
  const [mergeTarget, setMergeTarget] = useState<Position | null>(null)
  const [mergePreview, setMergePreview] = useState<PositionMergePreview | null>(null)
  const [mergePreviewError, setMergePreviewError] = useState<string | null>(null)
  const [merging, setMerging] = useState(false)

  const [createName, setCreateName] = useState('')
  const [creating, setCreating] = useState(false)

  const [editId, setEditId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)

  /** Должность, для которой открыт диалог подтверждения удаления (null — диалог закрыт). */
  const [confirmDelete, setConfirmDelete] = useState<Position | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    positionsApi
      .list(undefined, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setPositions(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить справочник'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [])

  const unverifiedCount = useMemo(() => positions.filter(isPendingReview).length, [positions])
  const hiddenCount = useMemo(() => positions.filter(isHiddenPosition).length, [positions])

  // Сортировка ≈ 7 тыс. строк — один раз на изменение списка, не на каждый символ поиска.
  const sorted = useMemo(
    () => [...positions].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
    [positions],
  )
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase().replaceAll('ё', 'е')
    return sorted.filter(
      (p) =>
        (filter === 'all' ||
          (filter === 'unverified' ? isPendingReview(p) : isHiddenPosition(p))) &&
        (!q || p.name.toLowerCase().replaceAll('ё', 'е').includes(q)),
    )
  }, [sorted, search, filter])
  const visible = filtered.slice(0, shown)

  // Новый поиск или фильтр — снова с первой порции.
  useEffect(() => {
    setShown(PAGE_SIZE)
  }, [search, filter])

  // Предпросмотр объединения — при выборе целевой должности.
  useEffect(() => {
    setMergePreview(null)
    setMergePreviewError(null)
    if (!mergeSource || !mergeTarget || mergeTarget.id === mergeSource.id) return
    const controller = new AbortController()
    adminApi.positions
      .mergePreview(mergeSource.id, mergeTarget.id, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setMergePreview(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setMergePreviewError(getErrorMessage(err, 'Не удалось подсчитать, что изменится'))
      })
    return () => controller.abort()
  }, [mergeSource, mergeTarget])

  /** Обновляет запись в списке (ответ сервера после правки/отметки). */
  function replacePosition(updated: Position) {
    setPositions((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
  }

  async function markVerified(p: Position) {
    setActionError(null)
    setNotice(null)
    setBusyId(p.id)
    try {
      replacePosition(await adminApi.positions.setVerified(p.id, true))
      invalidatePositionsCache()
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось отметить должность'))
    } finally {
      setBusyId(null)
    }
  }

  async function setHidden(p: Position, hidden: boolean) {
    setActionError(null)
    setNotice(null)
    setBusyId(p.id)
    try {
      replacePosition(await adminApi.positions.setHidden(p.id, hidden))
      invalidatePositionsCache()
      setNotice(
        hidden
          ? `Должность «${p.name}» скрыта: в подсказках её больше нет. Найти её и вернуть можно на вкладке «Скрытые».`
          : `Должность «${p.name}» снова показывается в подсказках.`,
      )
    } catch (err) {
      setActionError(getErrorMessage(err, hidden ? 'Не удалось скрыть должность' : 'Не удалось показать должность'))
    } finally {
      setBusyId(null)
    }
  }

  function openMerge(p: Position) {
    setActionError(null)
    setNotice(null)
    setMergeTarget(null)
    setMergeSource(p)
  }

  function closeMerge() {
    if (merging) return
    setMergeSource(null)
    setMergeTarget(null)
  }

  async function confirmMerge() {
    if (!mergeSource || !mergeTarget) return
    setMerging(true)
    try {
      const res = await adminApi.positions.merge(mergeSource.id, mergeTarget.id)
      const sourceID = mergeSource.id
      setPositions((prev) =>
        prev.filter((p) => p.id !== sourceID).map((p) => (p.id === res.target.id ? res.target : p)),
      )
      setNotice(
        `Должность «${mergeSource.name}» объединена с «${res.target.name}». ` +
          (res.workplaces > 0
            ? `Изменено ${res.workplaces} ${pluralRu(res.workplaces, 'рабочее место', 'рабочих места', 'рабочих мест')}.`
            : 'Рабочих мест с этой должностью не было.'),
      )
      invalidatePositionsCache()
      setMergeSource(null)
      setMergeTarget(null)
    } catch (err) {
      setMergeSource(null)
      setMergeTarget(null)
      setActionError(getErrorMessage(err, 'Не удалось объединить должности'))
    } finally {
      setMerging(false)
    }
  }

  async function create() {
    setActionError(null)
    setNotice(null)
    setCreating(true)
    try {
      const position = await adminApi.positions.create({ name: createName.trim() })
      setPositions((prev) => [...prev, position])
      setCreateName('')
      invalidatePositionsCache()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setActionError(
          `Должность «${createName.trim()}» уже есть в справочнике — найдите её поиском (она может быть на вкладке «Не проверенные» или «Скрытые»).`,
        )
      } else {
        setActionError(getErrorMessage(err, 'Не удалось создать должность'))
      }
    } finally {
      setCreating(false)
    }
  }

  function startEdit(p: Position) {
    setEditId(p.id)
    setEditName(p.name)
  }

  async function saveEdit() {
    if (!editId) return
    setActionError(null)
    setNotice(null)
    setSavingEdit(true)
    try {
      const updated = await adminApi.positions.update(editId, { name: editName.trim() })
      replacePosition(updated)
      setEditId(null)
      invalidatePositionsCache()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const wanted = editName.trim().toLowerCase()
        const clash = positions.find((p) => p.id !== editId && p.name.trim().toLowerCase() === wanted)
        setActionError(
          clash && isHiddenPosition(clash)
            ? `Должность «${editName.trim()}» уже есть в справочнике, но скрыта из подсказок. Откройте вкладку «Скрытые», нажмите у неё «Показать», затем у этой должности — «Объединить».`
            : `Должность «${editName.trim()}» уже есть в справочнике. Чтобы перенести на неё рабочие места, нажмите «Объединить».`,
        )
      } else {
        setActionError(getErrorMessage(err, 'Не удалось сохранить должность'))
      }
    } finally {
      setSavingEdit(false)
    }
  }

  async function remove(p: Position) {
    setActionError(null)
    setNotice(null)
    setBusyId(p.id)
    try {
      await adminApi.positions.delete(p.id)
      setPositions((prev) => prev.filter((x) => x.id !== p.id))
      invalidatePositionsCache()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setActionError(
          `Должность «${p.name}» используется в структуре или заказах — удалить нельзя. ` +
            (isHiddenPosition(p)
              ? 'Она уже скрыта из подсказок.'
              : 'Чтобы убрать её из подсказок, нажмите «Скрыть»; если такая должность уже есть в справочнике, нажмите «Объединить».'),
        )
      } else {
        setActionError(getErrorMessage(err, 'Не удалось удалить должность'))
      }
    } finally {
      setBusyId(null)
    }
  }

  /** Подтверждение в диалоге: закрываем диалог и запускаем удаление (409-обработка — в `remove`). */
  function confirmAndRemove() {
    if (!confirmDelete) return
    const target = confirmDelete
    setConfirmDelete(null)
    void remove(target)
  }

  return (
    <div>
      <PageHead
        title="Справочник должностей"
        subtitle="Единый список должностей для структуры, СОУТ, обучения и профрисков. Правит только администратор."
      />

      {actionError ? (
        <Alert variant="danger" className="mb-4">
          {actionError}
        </Alert>
      ) : null}
      {notice ? (
        <Alert variant="info" className="mb-4">
          {notice}
        </Alert>
      ) : null}

      <Card header="Новая должность" className="mb-4">
        <PositionForm
          name={createName}
          setName={setCreateName}
          busy={creating}
          submitLabel="Добавить должность"
          onSubmit={() => void create()}
        />
      </Card>

      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading ? (
        <Panel>
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        </Panel>
      ) : positions.length === 0 ? (
        <Panel>
          <EmptyState
            icon={Briefcase}
            title="Должностей пока нет"
            description="Добавьте первую должность — она станет доступна для выбора в структуре и формах услуг"
          />
        </Panel>
      ) : (
        <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
          <TabsList aria-label="Какие должности показать" className="mb-4">
            <TabsTrigger value="all">Все ({positions.length})</TabsTrigger>
            <TabsTrigger value="unverified">Не проверенные ({unverifiedCount})</TabsTrigger>
            <TabsTrigger value="hidden">Скрытые ({hiddenCount})</TabsTrigger>
          </TabsList>
          <TabsContent value={filter} className="pt-0">
            {filter === 'unverified' ? (
              <p className="mb-4 max-w-prose text-14 text-txt-secondary">
                Эти должности ещё не проверены: их ввели клиенты или они похожи на опечатку. В
                подсказках они не показываются, пока вы не отметите их проверенными. Опечатку
                исправьте кнопкой «Править», а если такая должность уже есть в справочнике, нажмите
                «Объединить». Если должность неверная, но удалить её нельзя (она уже есть в
                заказах), нажмите «Скрыть»: из подсказок и из этого списка она пропадёт.
              </p>
            ) : null}
            {filter === 'hidden' ? (
              <p className="mb-4 max-w-prose text-14 text-txt-secondary">
                Скрытые должности не показываются в подсказках и не попадают в список «Не
                проверенные». Они остаются в справочнике, в структуре клиентов и в заказах. Нажмите
                «Показать», чтобы вернуть должность.
              </p>
            ) : null}
            <Toolbar aria-label="Поиск должности">
              <div className="max-w-narrow flex-1">
                <Input
                  placeholder="Поиск по названию"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Поиск по названию"
                />
              </div>
            </Toolbar>

            {filtered.length === 0 ? (
              <Panel>
                <EmptyState
                  icon={Briefcase}
                  title={emptyTitle(filter, search)}
                  description={emptyDescription(filter, search)}
                />
              </Panel>
            ) : (
              <Panel>
                <DataTable>
                  <DataTableHead>
                    <tr>
                      <DataTableHeaderCell>Название</DataTableHeaderCell>
                      <DataTableHeaderCell>{''}</DataTableHeaderCell>
                    </tr>
                  </DataTableHead>
                  <DataTableBody>
                    {visible.map((p) =>
                      editId === p.id ? (
                        <DataTableRow key={p.id}>
                          <DataTableCell colSpan={2}>
                            <PositionForm
                              name={editName}
                              setName={setEditName}
                              busy={savingEdit}
                              submitLabel="Сохранить"
                              onSubmit={() => void saveEdit()}
                              onCancel={() => setEditId(null)}
                            />
                          </DataTableCell>
                        </DataTableRow>
                      ) : (
                        <DataTableRow key={p.id}>
                          <DataTableCell className="font-medium text-txt">
                            {p.name}
                            {isSentinel(p.name) ? (
                              <Badge variant="gray" className="ml-2">
                                служебная
                              </Badge>
                            ) : isHiddenPosition(p) ? (
                              <Badge variant="gray" className="ml-2">
                                скрыта
                              </Badge>
                            ) : !isVerifiedPosition(p) ? (
                              <Badge variant="amber" className="ml-2">
                                не проверена
                              </Badge>
                            ) : null}
                          </DataTableCell>
                          <DataTableCell className="text-right">
                            {/* Кнопки без иконок: с ними пять действий не помещаются в строку при ширине 1280 px. */}
                            <div className="flex flex-wrap justify-end gap-2">
                              {isPendingReview(p) ? (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={busyId === p.id}
                                  onClick={() => void markVerified(p)}
                                >
                                  Проверена
                                </Button>
                              ) : null}
                              <Button size="sm" variant="secondary" onClick={() => startEdit(p)}>
                                Править
                              </Button>
                              {!isSentinel(p.name) ? (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={busyId === p.id}
                                  onClick={() => openMerge(p)}
                                >
                                  Объединить
                                </Button>
                              ) : null}
                              {!isSentinel(p.name) ? (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={busyId === p.id}
                                  onClick={() => void setHidden(p, !isHiddenPosition(p))}
                                >
                                  {isHiddenPosition(p) ? 'Показать' : 'Скрыть'}
                                </Button>
                              ) : null}
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={busyId === p.id}
                                onClick={() => setConfirmDelete(p)}
                              >
                                Удалить
                              </Button>
                            </div>
                          </DataTableCell>
                        </DataTableRow>
                      ),
                    )}
                  </DataTableBody>
                </DataTable>
                {filtered.length > visible.length ? (
                  <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
                    <span className="text-13 text-txt-secondary">
                      Показано {visible.length} из {filtered.length}
                    </span>
                    <Button size="sm" variant="secondary" onClick={() => setShown((n) => n + PAGE_SIZE)}>
                      Показать ещё
                    </Button>
                  </div>
                ) : null}
              </Panel>
            )}
          </TabsContent>
        </Tabs>
      )}

      <ConfirmDialog
        open={mergeSource !== null}
        title={mergeSource ? `Объединить должность «${mergeSource.name}»` : undefined}
        message={
          mergeSource
            ? `Рабочие места и профриски с должностью «${mergeSource.name}» перейдут на выбранную должность, ` +
              'а эта запись будет удалена из справочника. Отправленные заказы не изменятся.'
            : undefined
        }
        details={
          mergeSource ? (
            <div className="mb-4 flex flex-col gap-3">
              <Field label="Объединить с должностью" required className="my-0">
                {(props) => (
                  <PositionSelect
                    id={props.id}
                    aria-describedby={props['aria-describedby']}
                    aria-required={props['aria-required']}
                    value={mergeTarget?.id}
                    onChange={(id, name) => setMergeTarget({ id, name, verified: true })}
                    disabled={merging}
                  />
                )}
              </Field>
              {mergeTarget && mergeTarget.id === mergeSource.id ? (
                <Alert variant="danger">Выберите другую должность — нельзя объединить должность с самой собой.</Alert>
              ) : null}
              {mergePreviewError ? <Alert variant="danger">{mergePreviewError}</Alert> : null}
              {mergePreview ? (
                <>
                  <p className="text-14 text-txt">{mergePreviewText(mergePreview)}</p>
                  {mergePreview.duplicatePairs > 0 ? (
                    <Alert variant="info">
                      {`В ${mergePreview.duplicatePairs} ${pluralRu(mergePreview.duplicatePairs, 'подразделении', 'подразделениях', 'подразделениях')} ` +
                        `окажутся два рабочих места с должностью «${mergeTarget?.name}». Места не объединяются — ` +
                        'у них свои сотрудники и история; при необходимости поправьте структуру клиента.'}
                    </Alert>
                  ) : null}
                  {mergePreview.conflicts > 0 ? (
                    <Alert variant="danger">
                      Объединить нельзя: у клиента есть результаты профрисков обеих должностей в одном
                      подразделении одного заказа. Исправьте результаты или выберите другую должность.
                    </Alert>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : undefined
        }
        buttons={[
          {
            label: merging ? 'Объединение…' : 'Объединить',
            disabled: merging || !mergePreview || mergePreview.conflicts > 0,
            onClick: () => void confirmMerge(),
          },
          { label: 'Отмена', variant: 'secondary', disabled: merging, onClick: closeMerge },
        ]}
        onClose={merging ? undefined : closeMerge}
      />

      <Dialog
        open={confirmDelete !== null}
        onOpenChange={(next) => {
          if (!next) setConfirmDelete(null)
        }}
      >
        {confirmDelete ? (
          <DialogContent
            title={`Удалить должность «${confirmDelete.name}»?`}
            description="Действие необратимо. Если должность используется в структуре или заказах, сервер откажет в удалении — тогда нажмите «Скрыть»."
            className="max-w-sm"
          >
            <Card header={`Удалить должность «${confirmDelete.name}»?`}>
              <p className="text-txt-secondary">
                Действие необратимо. Если должность используется в структуре или заказах — сервер
                откажет в удалении и покажет причину; тогда нажмите «Скрыть», и она пропадёт из подсказок.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="border-red bg-red text-white hover:opacity-90"
                  onClick={confirmAndRemove}
                  leftIcon={<Trash2 className="size-4 stroke-current" aria-hidden />}
                >
                  Удалить
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setConfirmDelete(null)}>
                  Отмена
                </Button>
              </div>
            </Card>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  )
}
